import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  parseStatementXmlSegments,
  segmentsToSentenceRows,
  StatementXmlPreview,
  statementXmlToPlainText,
} from '../statement-nodes';
import { TranslationSummary } from './types';
import { getLanguageDisplayName } from './utils';

interface TranslationReviewPanelProps {
  translation: TranslationSummary;
  productId: string;
  isBase?: boolean;
  expanded?: boolean;
  collapsible?: boolean;
  onToggleExpanded?: () => void;
  onCopyText: (text: string) => void;
  onViewHistory: (headId: string, language: string) => void;
  onEditTranslation: (productId: string, language: string) => void;
}

export const TranslationReviewPanel: React.FC<TranslationReviewPanelProps> = ({
  translation,
  productId,
  isBase = false,
  expanded = true,
  collapsible = false,
  onToggleExpanded,
  onCopyText,
  onViewHistory,
  onEditTranslation,
}) => {
  const { t } = useTranslation();
  const languageLabel = getLanguageDisplayName(translation.language);
  const latestText = translation.latest_text || '';
  const plainText = useMemo(() => statementXmlToPlainText(latestText), [latestText]);
  const codes = useMemo(() => {
    const rows = segmentsToSentenceRows(parseStatementXmlSegments(latestText));
    const orderedCodes: string[] = [];
    rows.forEach((row) => {
      if (row.code && !orderedCodes.includes(row.code)) orderedCodes.push(row.code);
    });
    return orderedCodes;
  }, [latestText]);
  const visibleCodes = codes.slice(0, 4);
  const remainingCodeCount = Math.max(codes.length - visibleCodes.length, 0);

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        border: '1px solid',
        borderColor: isBase ? 'primary.main' : 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        transition: 'border-color 160ms ease, background-color 160ms ease',
        '&:hover': {
          borderColor: isBase ? 'primary.dark' : theme.palette.primary.main,
          backgroundColor: isBase ? theme.palette.background.paper : 'rgba(63, 156, 70, 0.02)',
        },
      })}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0} flex={1}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: isBase ? 'primary.main' : 'primary.light',
                color: isBase ? 'primary.contrastText' : 'primary.dark',
                fontSize: '0.78rem',
                fontWeight: 700,
                flex: '0 0 auto',
              }}
            >
              {translation.language.toUpperCase()}
            </Avatar>
            <Box minWidth={0}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                  {languageLabel}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={isBase ? 'primary' : 'default'}
                  label={isBase ? t('productDetails.translationKindBase') : t('productDetails.translationKindTranslated')}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('productDetails.versionUpdated', {
                  version: translation.latest_version,
                  date: new Date(translation.updated_at).toLocaleDateString(),
                })}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEditTranslation(productId, translation.language)}
            >
              {t('productDetails.edit')}
            </Button>
            {translation.latest_version > 1 && (
              <Button
                size="small"
                variant="text"
                startIcon={<HistoryIcon fontSize="small" />}
                onClick={() => onViewHistory(translation.head_id, translation.language)}
              >
                {t('productDetails.viewHistory')}
              </Button>
            )}
            {translation.latest_text && (
              <Tooltip title={t('productDetails.copyText')}>
                <IconButton
                  size="small"
                  aria-label={t('productDetails.copyText')}
                  onClick={() => onCopyText(translation.latest_text || '')}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {collapsible && (
              <Tooltip title={expanded ? t('productDetails.collapseTranslation') : t('productDetails.expandTranslation')}>
                <IconButton
                  size="small"
                  aria-label={expanded ? t('productDetails.collapseTranslation') : t('productDetails.expandTranslation')}
                  onClick={onToggleExpanded}
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 160ms ease',
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {collapsible && !expanded && (
          <Stack spacing={1.1} sx={{ mt: 1.5, pl: { xs: 0, sm: 5.75 } }}>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {t('productDetails.clpStatementCount', { count: codes.length })}
              </Typography>
              {visibleCodes.map((code) => (
                <Chip key={code} size="small" color="warning" label={code} />
              ))}
              {remainingCodeCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('productDetails.moreClpCodes', { count: remainingCodeCount })}
                />
              )}
            </Stack>
            {plainText ? (
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                  lineHeight: 1.65,
                }}
              >
                {plainText}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('productDetails.noContentYet')}
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      <Collapse in={expanded} timeout={160} unmountOnExit={collapsible}>
        <Box sx={{ px: 2.5, py: 2.25 }}>
          {translation.latest_text ? (
            <StatementXmlPreview
              value={translation.latest_text}
              previewMode="codesAndSentences"
              sx={{ minHeight: 24 }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('productDetails.noContentYet')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};
